import type { Breakpoint } from '@mui/material/styles';
import type { NavSectionProps } from 'src/components/nav-section';
import type { MainSectionProps, HeaderSectionProps, LayoutSectionProps } from '../core';

import { merge } from 'es-toolkit';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

import { Logo } from 'src/components/logo';
import { useSettingsContext } from 'src/components/settings';

import { useAuthContext } from 'src/auth/hooks';

import { NavBottom } from './nav-bottom';
import { NavMobile } from './nav-mobile';
import { NavVertical } from './nav-vertical';
import { useTranslate } from 'src/locales';

import { _account } from '../nav-config-account';
import { MenuButton } from '../components/menu-button';
import { getNavDataByRole, makeCanDisplayItemByRole } from './nav-data';
import { AccountDrawer } from '../components/account-drawer';
import { dashboardLayoutVars, dashboardNavColorVars } from './css-vars';
import { NotificationsDrawer } from '../components/notifications-drawer';
import { ContactSupportButton } from '../components/contact-support-button';
import { MainSection, layoutClasses, HeaderSection, LayoutSection } from '../core';

// ----------------------------------------------------------------------

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type DashboardLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    nav?: {
      data?: NavSectionProps['data'];
    };
    main?: MainSectionProps;
  };
};

export function DashboardLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'sm',
}: DashboardLayoutProps) {
  const theme = useTheme();

  const { userRole } = useAuthContext();

  const { t } = useTranslate('nav');

  const settings = useSettingsContext();

  const navVars = dashboardNavColorVars(theme, settings.state.navColor, settings.state.navLayout);

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const isNavMini = settings.state.navLayout === 'mini';

  const navData = slotProps?.nav?.data ?? getNavDataByRole(userRole, t);

  const canDisplayItemByRole = makeCanDisplayItemByRole(userRole);

  const renderHeader = () => {
    const headerSlotProps: HeaderSectionProps['slotProps'] = {
      container: {
        maxWidth: false,
        sx: { px: { [layoutQuery]: 5 } },
      },
    };

    const headerSlots: HeaderSectionProps['slots'] = {
      topArea: (
        <Alert severity="warning" sx={{ display: 'none', py: 0, borderRadius: 0 }}>
          This is a test version of the app.
        </Alert>
      ),
      bottomArea: null,
      leftArea: (
        <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto', gap: 0 }}>
          <MenuButton
            onClick={onOpen}
            sx={{ mr: 1, ml: -1, [theme.breakpoints.up(layoutQuery)]: { display: 'none' } }}
          />
          <NavMobile
            data={navData}
            open={open}
            onClose={onClose}
            cssVars={navVars.section}
            checkPermissions={canDisplayItemByRole}
          />

          <Logo
            sx={{
              [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
            }}
          />
        </Box>
      ),
      rightArea: (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 0.75 }, flexShrink: 0 }}
        >
          <ContactSupportButton />
          <NotificationsDrawer />
          <AccountDrawer data={_account(t)} sx={{ ml: 1 }} />
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderSidebar = () => (
    <NavVertical
      data={navData}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      checkPermissions={canDisplayItemByRole}
      onToggleNav={() =>
        settings.setField('navLayout', settings.state.navLayout === 'vertical' ? 'mini' : 'vertical')
      }
    />
  );

  const renderMain = () => (
    <MainSection
      {...slotProps?.main}
      sx={[
        {
          pb: { xs: 'var(--layout-nav-bottom-height)', [layoutQuery]: 0 },
        },
        ...(Array.isArray(slotProps?.main?.sx) ? slotProps.main.sx : [slotProps?.main?.sx]),
      ]}
    >
      {children}
    </MainSection>
  );

  return (
    <LayoutSection
      headerSection={renderHeader()}
      sidebarSection={renderSidebar()}
      footerSection={<NavBottom data={navData} checkPermissions={canDisplayItemByRole} />}
      cssVars={{ ...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars }}
      sx={[
        {
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
